import React, { createContext, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { AiButton } from "../../../components/shared/ai-button";
import { BinocularsIcon, CalendarPlusIcon, FilePenIcon } from "lucide-react";
import { z } from "zod";
import { projectTaskSchema } from "tekkr-common/dist/model/project/project";
import { ScheduleMeetingFlow } from "./ai-task-flows/schedule-meeting-task-flow";
import { ResearchFlow } from "./ai-task-flows/research-task-flow";
import { DraftDocumentFlow } from "./ai-task-flows/draft-document-task-flow";
import { useProject } from "../../../data/project";
import { useOrg } from "../../../auth/org-provider";
import { useConnectOAuthService } from "../../../data/hooks/connect-oauth-account-button";
import {
    TaskExecutionResponse,
    ScheduleMeetingResponse,
    DraftDocumentResponse,
    ResearchResponse,
} from "../../../data/types/ai-task-types";
import { OAuthService } from "tekkr-common/dist/oauth";
import { useExecuteTaskMutation } from "../../../data/mutations/execute-task";

type Task = z.infer<typeof projectTaskSchema>;
export type AiTask = Task & { execution: { targetActor: "bot" } };

interface TaskFlowContext {
    setTaskComplete: () => void;
    setCancel: () => void;
}

const TaskFlowContext = createContext<TaskFlowContext>({
    setTaskComplete: () => {},
    setCancel: () => {},
});

export const useTaskFlow = () => useContext(TaskFlowContext);

const taskButtonMetadata: Record<
    AiTask["execution"]["type"],
    { icon: React.FC<{ className?: string }>; label: string }
> = {
    schedule_meeting: {
        icon: CalendarPlusIcon,
        label: "Schedule Meeting",
    },
    draft_document: {
        icon: FilePenIcon,
        label: "Draft Document",
    },
    do_research: {
        icon: BinocularsIcon,
        label: "Run Research",
    },
};

interface AiTaskButtonProps {
    task: AiTask;
    onTaskComplete: () => void;
}

function oauthServiceForOfficeSuite(provider: "google" | "microsoft"): OAuthService {
    if (provider === "google") {
        return OAuthService.Google;
    } else if (provider === "microsoft") {
        return OAuthService.Microsoft;
    }
    throw new Error(`unknown office suite provider: ${provider}`);
}

export function AiTaskButton({ task, onTaskComplete }: AiTaskButtonProps) {
    const { project } = useProject();
    const org = useOrg();
    const { connect: connectOAuth } = useConnectOAuthService();

    const taskButton = taskButtonMetadata[task.execution.type];

    const _executeTaskMutation = useExecuteTaskMutation();
    const executeTask = useMutation({
        mutationFn: async (): Promise<TaskExecutionResponse> => {
            await connectOAuth(oauthServiceForOfficeSuite(org.officeSuiteProvider));
            return await _executeTaskMutation.mutateAsync({
                projectId: project.id,
                taskId: task.id,
            });
        },
    });

    const taskContext: TaskFlowContext = {
        setTaskComplete: () => {
            executeTask.reset();
            onTaskComplete();
        },
        setCancel: () => {
            executeTask.reset();
        },
    };

    const handleClick = () => {
        executeTask.mutate();
    };

    const Icon = taskButton.icon;

    return (
        <TaskFlowContext.Provider value={taskContext}>
            <AiButton
                variant={"ghost"}
                size={"sm"}
                onClick={handleClick}
                isLoading={executeTask.isPending}
                className="h-8 shrink-0 bg-primary/10 text-primary hover:bg-primary/20"
            >
                <Icon className="me-2 h-4 w-4" />
                {taskButton.label}
            </AiButton>
            {executeTask.data && getTaskFlowComponent(task, executeTask.data)}
        </TaskFlowContext.Provider>
    );
}

function getTaskFlowComponent(task: AiTask, data: TaskExecutionResponse) {
    if (task.execution.type === "schedule_meeting") {
        return <ScheduleMeetingFlow data={data as ScheduleMeetingResponse} />;
    } else if (task.execution.type === "draft_document") {
        return <DraftDocumentFlow data={data as DraftDocumentResponse} />;
    } else if (task.execution.type === "do_research") {
        return <ResearchFlow data={data as ResearchResponse} />;
    }
    return null;
}
